import { useEffect, useMemo } from 'react';
import type { ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore } from 'react-redux';
import { toast } from 'sonner';
import type { TAnyClientQuestionType } from '@hubertai/screening-question-types';
import { CREATE_MODES, useQuestionEditorContext, ECreateQuestionMode } from '../context/create-question.context';
import { validateNewQuestion, validateUpdatedQuestion } from '../utils';
import { lacksQuestionUpdatePermission } from '../questionUpdatePermission';
import { HubertButton } from 'components/HubertUI/HubertButton';
import HubertTooltip from 'components/HubertUI/HubertTooltip';
import { useTemplateBuilderContext } from 'components/Templates/builder/context/template-builder.context';
import { useUser } from 'hooks/useUser';
import { questionsApi, useCreateQuestionMutation } from 'rtk/query/apis/questions';
import { useAppDispatch, useAppSelector } from 'rtk/query/hooks';
import { setRefreshPreview } from 'rtk/Question.Editor.Slice';
import { makeSelectFullQuestion, selectQuestionLanguage, selectQuestionMode } from 'rtk/questionEditorSelector';
import { setCurrentQuestion, updateQuestion } from 'rtk/QuestionSlice';
import { ETemplateRevision } from 'rtk/TemplateSlice';
import { tryGetErrorMessage } from 'utils/tryGetErrorMessage';
import { toastRuleReferenceBlockedError } from 'utils/toastRuleReferenceBlockedError';
import type { RootState } from 'app/store';

interface SaveQuestionProps extends Omit<ComponentProps<typeof HubertButton>, 'onClick'> {
  buttonText?: string;
  onClick?: (q: TAnyClientQuestionType) => void;
  onCreateQuestionFinished?: (q: TAnyClientQuestionType) => void;
  originalQuestion: TAnyClientQuestionType | null | undefined;
  onBusyChange?: (busy: boolean) => void;
}

export const SaveQuestion = ({
  buttonText,
  onClick,
  onCreateQuestionFinished,
  originalQuestion,
  onBusyChange,
  ...props
}: SaveQuestionProps) => {
  const { t } = useTranslation('Questionsv2');
  const user = useUser();

  const dispatch = useAppDispatch();
  const store = useStore<RootState>();
  const { questionId } = useQuestionEditorContext();
  const selectFullQuestionForId = useMemo(() => makeSelectFullQuestion(), []);

  const mode = useAppSelector((state) => selectQuestionMode(state, questionId));
  const questionLanguage = useAppSelector((state) => selectQuestionLanguage(state, questionId));
  const q = useAppSelector((state) => selectFullQuestionForId(state, questionId));
  const isPublishedOrNonExclusiveQuestion = useAppSelector(
    (state) => state.questionEditor.byId[questionId].isPublishedOrNonExclusiveQuestion,
  );
  const hasQuestionUpdatePermission = useAppSelector(
    (state) => state.questionEditor.byId[questionId].hasQuestionUpdatePermission,
  );
  const visibility = useAppSelector((state) => state.questionEditor.byId[questionId].visibility);

  const [createQuestionRequest, { isLoading }] = useCreateQuestionMutation();

  useEffect(() => {
    onBusyChange?.(isLoading);
  }, [isLoading, onBusyChange]);

  const { revision } = useTemplateBuilderContext();
  const isInDraft = revision === ETemplateRevision.Draft;
  // Exclusivity ("this question is shared/published") only blocks direct editing while in draft —
  // outside a draft nothing here is editable anyway, so that specific tooltip would be redundant.
  const showExclusivityTooltip =
    mode === ECreateQuestionMode.InTemplateEdit && isPublishedOrNonExclusiveQuestion && isInDraft;
  // Lacking question:update is a hard permission boundary, unrelated to draft status — it doesn't
  // come and go with the template's revision, so unlike exclusivity above this must NOT require
  // isInDraft, or the tooltip silently disappears while viewing a template's published revision.
  const showPermissionTooltip = lacksQuestionUpdatePermission(mode, hasQuestionUpdatePermission);
  const showUneditableTooltip = showExclusivityTooltip || showPermissionTooltip;
  // Standalone editing is never blocked by exclusivity (that's a deliberate product decision) —
  // this is a heads-up only, not a disabled state, so it doesn't need isInDraft's template concept.
  const showNonExclusiveWarningTooltip = mode === ECreateQuestionMode.Edit && isPublishedOrNonExclusiveQuestion;
  const showToolTip = showUneditableTooltip || showNonExclusiveWarningTooltip;

  const defaultCreate = () => {
    const question = validateNewQuestion(q, questionLanguage, user, mode, originalQuestion);

    if (question) {
      createQuestionRequest(question)
        .unwrap()
        .then(async (result) => {
          if (result.question) {
            toast.success(t('saveQuestion.toast.saved', { id: q.id }));
            dispatch(setRefreshPreview({ questionId }));
            if (onCreateQuestionFinished) {
              onCreateQuestionFinished(result.question);
            }
          } else {
            toast.error(`${result}`);
          }
        })
        .catch((e: unknown) => {
          if (toastRuleReferenceBlockedError(e, store.getState().Library)) return;

          const errorMessage = tryGetErrorMessage(e);
          if (errorMessage) {
            toast.error(errorMessage);
            return;
          }

          toast.error(t('saveQuestion.toast.somethingWentWrong', { error: String(e) }));
        });
    } else {
      toast.error(t('saveQuestion.toast.validationFailed'));
    }
  };

  const defaultUpdate = () => {
    let question: TAnyClientQuestionType | undefined;
    if (originalQuestion) {
      question = validateUpdatedQuestion(originalQuestion, q, questionLanguage, user, mode);
    } else {
      toast.error(t('saveQuestion.toast.configNotAvailable'));
      return;
    }

    if (!question) {
      toast.error(t('saveQuestion.toast.updateValidationFailed'));
      return;
    }

    if (q._id)
      dispatch(
        setCurrentQuestion({
          question: {
            id: q._id,
            visibility: q.visibility ?? 'global',
          },
        }),
      );

    try {
      dispatch(updateQuestion({ question }));
      dispatch(questionsApi.endpoints.updateQuestion.initiate(question));
      dispatch(setRefreshPreview({ questionId }));
    } catch (error) {
      const errorMessage = tryGetErrorMessage(error);
      if (errorMessage) {
        toast.error(errorMessage);
        return;
      }

      toast.error(t('saveQuestion.toast.updateFailed'));
    }
  };

  return (
    <HubertTooltip>
      {/* A disabled native <button> never fires the pointer events HubertTooltip listens for
          (HubertButton applies disabled:pointer-events-none) — wrap it in a plain, always-enabled
          element so hovering over a disabled Save button can still open the tooltip. Same pattern
          as UneditableInfoTooltip. */}
      <HubertTooltip.Trigger asChild>
        <div className="inline-block">
          <HubertButton
            {...props}
            loading={isLoading}
            onClick={() => {
              const question = validateNewQuestion(q, questionLanguage, user, mode, originalQuestion);
              if (!question) {
                toast.error(t('saveQuestion.toast.updateValidationFailedShort'));
                return;
              }

              if ([...CREATE_MODES].includes(mode)) {
                if (onClick) {
                  onClick(question);
                } else {
                  defaultCreate();
                }
              } else if ([ECreateQuestionMode.Edit, ECreateQuestionMode.InTemplateEdit].includes(mode)) {
                if (onClick) {
                  onClick(question);
                } else {
                  defaultUpdate();
                }
              }
            }}
          >
            {buttonText ?? t('saveQuestion.defaultButton')}
          </HubertButton>
        </div>
      </HubertTooltip.Trigger>
      {showToolTip && (
        <HubertTooltip.Content>
          {showUneditableTooltip
            ? showPermissionTooltip
              ? mode === ECreateQuestionMode.InTemplateEdit
                ? t('builder.noPermissionTooltip')
                : visibility === 'global'
                  ? t('builder.libraryTooltipStandalone')
                  : t('builder.noPermissionTooltipStandalone')
              : t('builder.uneditableTooltip')
            : t('builder.nonExclusiveWarningTooltip')}
        </HubertTooltip.Content>
      )}
    </HubertTooltip>
  );
};
